import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { CreateAdminInput } from "@/api/routes/admins/types";
import Icon from "../../../lib/components/custom/Icon";
import {
	Avatar,
	AvatarFallback,
	AvatarImage,
} from "../../../lib/components/ui/avatar";
import { Badge } from "../../../lib/components/ui/badge";
import { Button } from "../../../lib/components/ui/button";
import { Card } from "../../../lib/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "../../../lib/components/ui/dialog";
import { Input } from "../../../lib/components/ui/input";
import { Label } from "../../../lib/components/ui/label";
import { Skeleton } from "../../../lib/components/ui/skeleton";
import { useApi } from "../../../lib/hooks/api/use-api";
import type { GetTypes } from "../../../lib/types/api-types";
import AdminLayout from "../layout";

export default function AdminAdmins() {
	const { t } = useTranslation();
	const [searchTerm, setSearchTerm] = useState("");
	const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

	const { admins, users } = useApi();

	// Fetch admins and users data
	const {
		data: adminsData,
		isLoading: adminsLoading,
		refetch: refetchAdmins,
	} = admins.getAll();
	const { data: usersData, isLoading: usersLoading } = users.getAll();

	// Get users who are not already admins
	const availableUsers = useMemo(() => {
		if (!usersData?.users || !adminsData?.admins) return [];

		const adminUserIds = new Set(
			adminsData.admins.map((admin) => admin.userId),
		);
		return usersData.users.filter((user) => !adminUserIds.has(user.id));
	}, [usersData, adminsData]);

	const filteredAdmins = useMemo(() => {
		if (!adminsData?.admins) return [];

		return adminsData.admins.filter((admin) => {
			const matchesSearch =
				admin.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
				admin.user?.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
				admin.user?.phone?.toLowerCase().includes(searchTerm.toLowerCase());

			return matchesSearch;
		});
	}, [adminsData, searchTerm]);

	const handleCreateAdmin = async (formData: FormData) => {
		try {
			const userId = formData.get("userId") as string;

			const data: CreateAdminInput = {
				userId,
			};

			await admins.create.mutateAsync(data);
			toast.success(t("admin.admins.created_success"));
			setIsCreateDialogOpen(false);
			refetchAdmins();
		} catch (error) {
			toast.error(t("admin.admins.create_failed"));
		}
	};

	const handleDeleteAdmin = async (adminId: number) => {
		if (!confirm(t("admin.admins.remove_confirm"))) {
			return;
		}

		try {
			await admins.delete.mutateAsync(adminId);
			toast.success(t("admin.admins.removed_success"));
			refetchAdmins();
		} catch (error) {
			toast.error(t("admin.admins.remove_failed"));
		}
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
		});
	};

	const getInitials = (name: string) => {
		return name
			.split(" ")
			.map((word) => word.charAt(0))
			.join("")
			.toUpperCase()
			.slice(0, 2);
	};

	if (adminsLoading) {
		return (
			<AdminLayout>
				<div className="space-y-6">
					<div className="flex items-center justify-between">
						<Skeleton className="h-8 w-40" />
						<Skeleton className="h-10 w-32" />
					</div>
					<Card className="p-6">
						<Skeleton className="h-10 w-full" />
					</Card>
					<div className="space-y-4">
						{Array.from({ length: 5 }).map((_, i) => (
							<Card key={i} className="p-5">
								<div className="flex items-center gap-4">
									<Skeleton className="w-12 h-12 rounded-full" />
									<div className="flex-1 space-y-2">
										<Skeleton className="h-4 w-48" />
										<Skeleton className="h-3 w-32" />
									</div>
									<Skeleton className="h-8 w-20" />
								</div>
							</Card>
						))}
					</div>
				</div>
			</AdminLayout>
		);
	}

	return (
		<AdminLayout>
			<div className="space-y-6">
				{/* Header */}
				<div className="flex items-center justify-between">
					<h1 className="text-2xl font-bold">{t("admin.admins.title")}</h1>
					<Dialog
						open={isCreateDialogOpen}
						onOpenChange={setIsCreateDialogOpen}
					>
						<DialogTrigger asChild>
							<Button>
								<Icon name="Plus" className="w-4 h-4 mr-2" />
								{t("admin.admins.add_admin")}
							</Button>
						</DialogTrigger>
						<DialogContent className="max-w-md">
							<DialogHeader>
								<DialogTitle>{t("admin.admins.add_new_admin")}</DialogTitle>
							</DialogHeader>
							<AdminForm
								availableUsers={availableUsers}
								onSubmit={handleCreateAdmin}
								isLoadingUsers={usersLoading}
							/>
						</DialogContent>
					</Dialog>
				</div>

				{/* Search */}
				<Card className="p-6">
					<div className="relative">
						<Icon
							name="Search"
							className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4"
						/>
						<Input
							placeholder={t("admin.admins.search_placeholder")}
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							className="pl-10"
						/>
					</div>
				</Card>

				{/* Admins List */}
				<div className="space-y-4">
					{filteredAdmins.length === 0 ? (
						<Card className="p-12 text-center">
							<Icon
								name="Shield"
								className="w-16 h-16 text-muted-foreground mx-auto mb-4"
							/>
							<h3 className="text-lg font-semibold mb-2">
								{t("admin.admins.no_admins_found")}
							</h3>
							<p className="text-muted-foreground">
								{searchTerm
									? t("admin.admins.no_admins_filtered")
									: t("admin.admins.no_admins_start")}
							</p>
						</Card>
					) : (
						<>
							<div className="text-sm text-muted-foreground mb-4">
								{t("admin.admins.count", {
									filtered: filteredAdmins.length,
									total: adminsData?.totalAdmins || 0,
								})}
							</div>
							{filteredAdmins.map((admin) => (
								<Card
									key={admin.id}
									className="p-5 hover:shadow-lg transition-all duration-200"
								>
									<div className="flex items-center gap-4">
										{/* Avatar */}
										<Avatar className="w-12 h-12">
											<AvatarImage
												src={admin.user?.image || ""}
												alt={admin.user?.name}
											/>
											<AvatarFallback>
												{admin.user?.name
													? getInitials(admin.user.name)
													: admin.user?.email.charAt(0).toUpperCase()}
											</AvatarFallback>
										</Avatar>

										{/* Admin Info */}
										<div className="flex-1 min-w-0">
											<div className="flex items-center gap-3 mb-1">
												<h3 className="font-semibold text-base truncate">
													{admin.user?.name}
												</h3>
												<Badge
													variant="secondary"
													className="bg-purple-100 text-purple-800"
												>
													<Icon name="Shield" className="w-3 h-3 mr-1" />
													{t("admin.sidebar.admins")}
												</Badge>
												{admin.user?.isActive ? (
													<Badge variant="default">
														{t("admin.users.active")}
													</Badge>
												) : (
													<Badge variant="secondary">
														{t("admin.users.inactive")}
													</Badge>
												)}
											</div>
											<div className="text-sm text-muted-foreground space-y-1">
												<div className="flex items-center gap-4">
													<span>{admin.user?.email}</span>
													{admin.user?.phone && <span>{admin.user.phone}</span>}
													<span>Points: {admin.user?.points || 0}</span>
												</div>
												<div>
													{t("admin.admins.admin_since", {
														date: formatDate(admin.createdAt || ""),
													})}
												</div>
											</div>
										</div>

										{/* Actions */}
										<div className="flex gap-2">
											<Button
												variant="outline"
												size="sm"
												onClick={() => handleDeleteAdmin(admin.id)}
												className="text-destructive hover:text-destructive"
											>
												<Icon name="UserMinus" className="w-4 h-4 mr-2" />
												{t("admin.admins.remove")}
											</Button>
										</div>
									</div>
								</Card>
							))}
						</>
					)}
				</div>
			</div>
		</AdminLayout>
	);
}

// Admin Form Component
function AdminForm({
	availableUsers,
	onSubmit,
	isLoadingUsers,
}: {
	availableUsers: GetTypes["users"]["getAll"] extends { users?: infer Users }
		? Users
		: any[];
	onSubmit: (formData: FormData) => void;
	isLoadingUsers: boolean;
}) {
	const { t } = useTranslation();
	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const formData = new FormData(e.currentTarget);
		onSubmit(formData);
	};

	return (
		<form onSubmit={handleSubmit} className="space-y-4">
			<div className="space-y-2">
				<Label htmlFor="userId">{t("admin.admins.select_user")}</Label>
				{isLoadingUsers ? (
					<Skeleton className="h-10 w-full" />
				) : (
					<select
						id="userId"
						name="userId"
						className="w-full h-10 px-3 py-2 text-sm border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
						required
					>
						<option value="">{t("admin.admins.choose_user")}</option>
						{availableUsers.map((user) => (
							<option key={user.id} value={user.id}>
								{user.name} ({user.email})
							</option>
						))}
					</select>
				)}
			</div>

			{availableUsers.length === 0 && !isLoadingUsers && (
				<div className="text-sm text-muted-foreground p-3 bg-muted rounded-md">
					<Icon name="Info" className="w-4 h-4 inline mr-2" />
					{t("admin.admins.all_admins_info")}
				</div>
			)}

			<div className="flex justify-end gap-3 pt-4">
				<Button
					type="submit"
					disabled={availableUsers.length === 0 || isLoadingUsers}
				>
					{t("admin.admins.add_admin")}
				</Button>
			</div>
		</form>
	);
}
